import React from "react";

import {
  Link as RouteLink,
  LinkProps as RouteLinkProps,
} from "react-router-dom";

import MuiLink, { LinkProps as MuiLinkProps } from "@mui/material/Link";
import styled from "@mui/material/styles/styled";

// eslint-disable-next-line lingui/no-unlocalized-strings
const NEW_TAB_PROPS = { rel: "noopener noreferrer", target: "_blank" };

export interface LinkProps
  extends Omit<MuiLinkProps, "underline" | "fontWeight"> {
  /** Will force the link to open in a new tab. Unnecessary to pass when also passing the `href` prop */
  newTab?: boolean;
  /** Path to open. Will open a new tab by default */
  href?: string;
  replace?: RouteLinkProps["replace"];
  state?: RouteLinkProps["state"];
  to?: RouteLinkProps["to"];
  fontWeight?: number;
  underline?: boolean;
}

interface StyledMuiLinkProps
  extends Omit<MuiLinkProps, "underline" | "fontWeight"> {
  to?: RouteLinkProps["to"];
  fontWeight?: number;
  underline?: boolean;
}

const MuiLinkWithRef = React.forwardRef<HTMLAnchorElement, StyledMuiLinkProps>(
  ({ fontWeight: _f, underline: _u, ...props }, ref) => (
    <MuiLink {...props} ref={ref} />
  ),
);

MuiLinkWithRef.displayName = "MuiLinkWithRef";

const StyledMuiLink = styled(MuiLinkWithRef)(
  ({ fontWeight, underline, onClick }) => ({
    fontWeight,
    textDecoration: underline ? "underline" : "none",
    ...(onClick && {
      "&:hover": { cursor: "pointer" },
    }),
  }),
);

/**
 * Generic Link component. Should be used for cases where the link won't ever be in the Zoom client or if
 * the link is simply a navigation to some place else within our application (w/o opening a new tab)
 * */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ children, to, href, newTab, ...props }, ref) => {
    // hrefs will always open a new tab
    const newTabProps = newTab || Boolean(href) ? NEW_TAB_PROPS : {};

    if (to) {
      return (
        <StyledMuiLink
          {...newTabProps}
          {...props}
          to={to}
          ref={ref}
          component={RouteLink}
        >
          {children}
        </StyledMuiLink>
      );
    }

    return (
      <StyledMuiLink {...newTabProps} {...props} ref={ref} href={href}>
        {children}
      </StyledMuiLink>
    );
  },
);

Link.displayName = "Link";

export default Link;
